type LogLevel = "log" | "info" | "warn" | "error";

type LogInfo = {
  prefix: string;
  msg: string;
  data: unknown;
  desc: string;
};

type LogColor = Partial<Record<LogLevel, string>>;

type CreateLoggerOptions = {
  onBefore?: (logInfo: LogInfo) => void;
  onAfter?: (logInfo: LogInfo) => void;
  prefix?: (() => string) | string;
  logColor?: LogColor;
  enableLog?: boolean | ((logInfo: LogInfo) => boolean);
};

type LogFunction = (msg: string, data?: unknown) => void;

export type Logger = Record<LogLevel, LogFunction>;

export const createLogger = (options: CreateLoggerOptions): Logger => {
  const {
    onBefore,
    onAfter,
    prefix,
    enableLog = true,
    logColor = {},
  } = options;
  const colors: Record<LogLevel, string> = {
    log: "green",
    info: "blue",
    warn: "orange",
    error: "red",
    ...logColor,
  };

  const getPrefix = () =>
    typeof prefix === "function" ? prefix() : prefix || "";

  const write = (level: LogLevel, msg: string, data?: unknown) => {
    const currentPrefix = getPrefix();
    const logInfo: LogInfo = {
      prefix: currentPrefix,
      msg,
      data,
      desc: currentPrefix + msg,
    };

    onBefore?.(logInfo);
    const shouldLog =
      typeof enableLog === "function" ? enableLog(logInfo) : enableLog;
    if (shouldLog) {
      console[level](`%c ${logInfo.desc}`, `color: ${colors[level]};`, data);
    }
    onAfter?.(logInfo);
  };

  return {
    log: (msg, data) => write("log", msg, data),
    info: (msg, data) => write("info", msg, data),
    warn: (msg, data) => write("warn", msg, data),
    error: (msg, data) => write("error", msg, data),
  };
};
